import { Card, CardContent, CardHeader, CardTitle } from "../../tailwindComponents/ui/card"
import { AlertCircle, ArrowDown, ArrowUp, BarChart3, Box, Calendar, DollarSign, ExternalLink, InfoIcon, Package, ShoppingBag, Tag, TrendingDown, TrendingUp, Truck } from "lucide-react"
import { ExecutiveSummary } from "../../tailwindComponents/dashboards/executive-summary"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "../../tailwindComponents/ui/tooltip"
import PerformanceDashboardService from "../../services/dashboards/PerformanceDashboard.service"
import { useEffect, useRef, useState } from "react"
import CurrentClientProvider from "../../providers/CurrentClientProvider"
import GlobalTip from "../../providers/GlobalTip"
import EpochMonthService from "../../services/EpochMonth.service"
import { ClientLevelItem, CategoryLevelItem, LocationLevelItem } from "../../interfaces/dashboards/PerformanceDashboard.interface"
import MonthSelector from "../../tailwindComponents/ui/dates/month-selector.component"
import InventoryHealth from "./performance-dashboard-components/InventoryHealth.component"
import ClassPerformance from "./performance-dashboard-components/ClassPerformance.component"
import LocationSalesBreakdown from "./performance-dashboard-components/LocationSalesBreakdown.component"
import { TrackableEvent, trackEventFromObject, trackEventFromErrorObject } from "../../services/AmplitudeService"

const AmplitudeEventPageName = "Performance Dashboard";
const InitialLoadTime = new Date().getTime();

let InitialVisitEvent = new TrackableEvent({
  pageName: AmplitudeEventPageName,
  clientCode: '',
  action: 'Initial Visit',
  additionalMetadata: {},
});

const defaultIconClassName = "h-4 w-4 text-muted-foreground";

const IconMapping = {
  DollarSign: <DollarSign className={defaultIconClassName} />,
  Package: <Package className={defaultIconClassName} />,
  BarChart3: <BarChart3 className={defaultIconClassName} />,
  ShoppingBag: <ShoppingBag className={defaultIconClassName} />,
  Calendar: <Calendar className={defaultIconClassName} />,
  Tag: <Tag className={defaultIconClassName} />,
  Truck: <Truck className={defaultIconClassName} />,
  Box: <Box className={defaultIconClassName} />,
  TrendingDown: (
    <TrendingDown
      className={`${defaultIconClassName} text-red-500`}
    />
  ),
  TrendingDownYellow: (
    <TrendingDown
      className={`${defaultIconClassName} text-yellow-500`}
    />
  ),
  TrendingUp: (
    <TrendingUp
      className={`${defaultIconClassName} text-emerald-500`}
    />
  ),
  TrendingUpRed: (
    <TrendingUp
      className={`${defaultIconClassName} text-red-500`}
    />
  ),
  RedAlert: (
    <AlertCircle
      className={`${defaultIconClassName} text-red-500`}
    />
  ),
};


const IconCardHeader = ({
  title,
  icon,
  description,
  className = null,
  onClick = null
}: {
  title: string;
  icon: keyof typeof IconMapping;
  description: string;
  className?: string | null;
  onClick?: (() => void) | null;
}) => {
  const IconComponent = IconMapping[icon] || DollarSign;

  const headerClassName = className ? className : "flex flex-row items-center justify-between space-y-0 px-2 py-0";

  return (
    <CardHeader className={headerClassName}>
      {/* Left Section: Icon and Title */}
      <div className="flex items-center space-x-2">
        {IconComponent && (
          <div className="flex h-6 w-6 items-center justify-center">
            {IconComponent}
          </div>
        )}
        <CardTitle className="text-md font-medium leading-none">{title}</CardTitle>
      </div>

      {/* Right Section: External Link and Info Tooltip */}
      <div className="flex items-center space-x-2">
        {onClick != null && (
          <button 
            onClick={onClick}
            className="flex h-5 w-5 items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
          </button>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <button className="flex h-5 w-5 items-center justify-center text-muted-foreground hover:text-foreground transition-colors cursor-help">
              <InfoIcon className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs bg-white" side="top" align="end">
            <p className="text-sm">{description}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </CardHeader>
  );
};

const QuickComparisonCard = ({
  title,
  value,
  comparisonValue,
  description,
  comparisonText,
  icon,
  onClick = null,
  children = null,
}: {
  title: string;
  value: number;
  comparisonValue: number;
  description: string;
  comparisonText: string;
  icon: keyof typeof IconMapping;
  onClick?: (() => void) | null;
  children?: JSX.Element | null;
}) => {
  // Calculate comparison color and label
  comparisonValue = value - comparisonValue;
  const comparisonColor = comparisonValue > 0 ? "text-green-500" : "text-red-500";
  const arrowDirection = comparisonValue > 0 ? "up" : "down";

  return (
    <Card className="rounded-xl p-2 bg-white">
      <IconCardHeader title={title} icon={icon} description={description} onClick={onClick} className={"flex flex-row items-center justify-between space-y-0 pb-2 pt-2 px-1"} />
      <CardContent className="px-1 pb-3 text-center">
        <div className="text-2xl font-bold">
          {PerformanceDashboardService.moneyFormat(value, true)}
        </div>
        {children}
        <div className="flex flex-row justify-center items-center text-xs text-muted-foreground mt-4">
          {arrowDirection == "down" ? (
            <ArrowDown className={`mr-1 h-3 w-3 ${comparisonColor}`} />
          ) : (
            <ArrowUp className={`mr-1 h-3 w-3 ${comparisonColor}`} />
          )}
          <span className={`${comparisonColor} font-medium`}>
            {`${PerformanceDashboardService.moneyFormat(Math.abs(comparisonValue), true)} ${comparisonText}`}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

const QuickInfoSidebarStyles = {
  'red': "w-2 rounded-l-xl bg-red-500 h-full z-50",
  'yellow': "w-2 rounded-l-xl bg-yellow-500 h-full z-50",
  'green': "w-2 rounded-l-xl bg-green-500 h-full z-50",
}

const QuickInfoCard = ({
  title,
  sidebarColor,
  value,
  tooltipText,
  description,
  color,
  icon,
  onClick = null,
}: {
  title: string;
  value: number;
  tooltipText: string;
  description: string|JSX.Element;
  color: string;
  icon: keyof typeof IconMapping;
  sidebarColor?: keyof typeof QuickInfoSidebarStyles;
  onClick?: (() => void) | null;
}) => {
  const sidebarStyle = QuickInfoSidebarStyles[sidebarColor as keyof typeof QuickInfoSidebarStyles] || null;

  return (
    <Card className="flex rounded-xl bg-white">
      {sidebarStyle && (
        <div className={sidebarStyle}></div>
      )}
      <div className="flex-1">
        <IconCardHeader title={title} icon={icon} description={tooltipText} onClick={onClick} className={"flex flex-row items-center justify-between space-y-0 pb-2 pt-4 px-3"} />
        <CardContent>
          <div className={`text-2xl font-bold text-${color}`}>
            {PerformanceDashboardService.moneyFormat(Math.abs(value), true)}
          </div>
          {description}
        </CardContent>
      </div>
    </Card>
  );
};

export default function Dashboard() {
  const rawDataReturn = useRef<any>(null)
  const currentClient = CurrentClientProvider.useClient() as any
  const globalTip = GlobalTip.useMessage() as any
  const [clientLevelData, setClientLevelData] = useState<ClientLevelItem[]|null>(null)
  const [thisMonthClientLevelData, setThisMonthClientLevelData] = useState<ClientLevelItem|null>(null)
  const [compareMonthClientLevelData, setCompareMonthClientLevelData] = useState<ClientLevelItem|null>(null)
  const [categoryLevelData, setCategoryLevelData] = useState<CategoryLevelItem[]|null>(null)
  const [locationLevelData, setLocationLevelData] = useState<LocationLevelItem[]|null>(null)
  const [locations, setLocations] = useState<string[]>([])
  const [selectedLocation, setSelectedLocation] = useState<string>("all-locations")
  const [compareEpochMonth, setCompareEpochMonth] = useState<number>(EpochMonthService.getTodayEpochMonth() - 1)
  const [epochMonth, setEpochMonth] = useState<number>(EpochMonthService.getTodayEpochMonth())
  const [compareEpochMonthOptions, setCompareEpochMonthOptions] = useState<{ label: string; value: number }[]>([]);

  function parseRawData(data: any) {
    // Make a deep copy of the data to avoid modifying the original
    const dataCopy = JSON.parse(JSON.stringify(data));

    if (selectedLocation !== "all-locations") {
      // Transform the copy, not the original data
      dataCopy.client = PerformanceDashboardService.convertLocationLevelToClientLevel(dataCopy.locations, selectedLocation);
      dataCopy.categories = PerformanceDashboardService.convertCategoryLocationLevelToCategoryLevel(dataCopy.categoryLocations, selectedLocation);
    }

    setClientLevelData(dataCopy.client);
    setThisMonthClientLevelData(dataCopy.client.find((item: ClientLevelItem) => item.epochMonthSpan === epochMonth));
    setCompareMonthClientLevelData(dataCopy.client.find((item: ClientLevelItem) => item.epochMonthSpan === compareEpochMonth));
    setCategoryLevelData(dataCopy.categories);
    setLocationLevelData(dataCopy.locations);

    // extract unique location codes
    const uniqueLocations = new Set<string>();
    dataCopy.locations.forEach((item: LocationLevelItem) => {
      uniqueLocations.add(item.locationCode);
    });

    // sort desc unique locations
    const sortedLocations = Array.from(uniqueLocations).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    setLocations(sortedLocations);
  }

  const loadData = async () => {
    rawDataReturn.current = null;
    setClientLevelData(null);
    // Don't reset selectedLocation here
    setLocations([]);
    setLocationLevelData(null);
    setCompareMonthClientLevelData(null);
    setThisMonthClientLevelData(null);
    setCategoryLevelData(null);

    PerformanceDashboardService.getSimpleDashboardDataForClient({
      clientId: currentClient.client.Id,
      epochMonth,
      compareEpochMonth
    }).then((data) => {
      try {
        // Store the original, unmodified data
        rawDataReturn.current = data;
        // Parse the data according to current selectedLocation
        parseRawData(data);
      } catch (error) {
        console.error(error);
        globalTip.show("Failed to parse data");
      }
    }).catch((error) => {
      console.error(error);
      globalTip.show("Failed to fetch data");
    });
  }

  useEffect(() => {
    if (rawDataReturn.current) {
      // Always parse from the original data
      parseRawData(rawDataReturn.current);
    }
  }, [selectedLocation, epochMonth, compareEpochMonth]); // Include all dependencies

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        const timeOnPage = Math.floor((new Date().getTime() - InitialLoadTime) / 1000);
        const ampEvent = new TrackableEvent({
          pageName: AmplitudeEventPageName,
          clientCode: currentClient.client.ClientCode,
          action: 'Navigate Out',
          additionalMetadata: {
            timeOnPage,
            epochMonth,
            compareEpochMonth,
          }
        });
        trackEventFromObject(ampEvent);
      }
    };
  
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
  }, []);

  function buildCompareEpochMonthOptions() {
    // from today epoch month - 23 max of selected epoch month - 1
    const todayEpochMonth = EpochMonthService.getTodayEpochMonth();

    const _epochMonth = epochMonth ? epochMonth : todayEpochMonth;

    const options = [];
    for (let i = _epochMonth - 1; i >= todayEpochMonth - 23; i--) {
      options.push({ label: `Compare to: ${EpochMonthService.epochMonthToMMMYY(i)}`, value: i });
    }

    setCompareEpochMonthOptions(options);
  }

  useEffect(() => {
    buildCompareEpochMonthOptions()
    if (compareEpochMonth == epochMonth - 1) return
    setCompareEpochMonth(epochMonth - 1)
  }, [epochMonth])

  useEffect(() => {
    if (!currentClient || !currentClient.client) return
    if (!currentClient.client?.Id) return
    loadData()
  }, [currentClient.client, epochMonth, compareEpochMonth])

  useEffect(() => {
    if (!currentClient || !currentClient.client) return

    InitialVisitEvent.clientCode = currentClient.client.ClientCode

    trackEventFromObject(InitialVisitEvent);
  }, [currentClient]);

  useEffect(() => {
    if (!currentClient || !currentClient.client) return

    const event = new TrackableEvent({
      pageName: AmplitudeEventPageName,
      clientCode: currentClient.client.ClientCode,
      action: 'Dashboard Filter Change',
      additionalMetadata: {
        selectedLocation: selectedLocation,
        epochMonth: epochMonth,
        compareEpochMonth: compareEpochMonth,
      }
    });

    trackEventFromObject(event);
  }, [selectedLocation])

  const SelectionHeader = () => {
    return (
      <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur">
        <div className="container flex h-16 items-center justify-between py-4">
          <div className="flex items-center gap-2 font-semibold">
            <ShoppingBag className="h-5 w-5" />
            <span>RetailORBIT ({currentClient.client?.Name}, {currentClient.client?.ClientCode})</span>
          </div>
          <div className="flex items-center gap-4">
            {compareEpochMonthOptions.length > 0 && (
              <select
                id="compareMonth"
                value={compareEpochMonth}
                onChange={(e) => {setCompareEpochMonth(Number(e.target.value))}}
                className="border border-gray-300 rounded p-2"
              >
                {compareEpochMonthOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
            <select
              id="selectLocation"
              value={selectedLocation}
              onChange={(e) => {setSelectedLocation(e.target.value)}}
              className="border border-gray-300 rounded p-2"
            >
              <option value="all-locations">All Locations</option>
              {locations.map((location) => (
                <option key={location} value={location}>
                  {location}
                </option>
              ))}
            </select>
            <MonthSelector
              epochMonth={epochMonth}
              setEpochMonth={setEpochMonth}
              minMonth={EpochMonthService.getTodayEpochMonth() - 11}
            />
          </div>
        </div>
      </header>
    )
  }

  if (!clientLevelData || !categoryLevelData || !thisMonthClientLevelData) {
    return (
      <div className="flex min-h-screen items-center justify-center flex-col h-full w-full">
        <div className="min-w-full">
          <SelectionHeader />
        </div>
        <div className="text-center h-full w-full">
          <p className="text-lg font-semibold">Loading... should only take a few seconds</p>
        </div>
      </div>
    )
  }

  // Get sum of epochMonth - epochMonth + 2 for otb
  const otbRetailEomNext3Months = clientLevelData.filter((item) => item.epochMonthSpan >= epochMonth && item.epochMonthSpan <= epochMonth + 2).reduce((acc, item) => acc + item.otbRetailEom, 0);

  const overstockedClasses = categoryLevelData.filter((item) => item.inventoryBomRm > item.inventoryBomPlan && item.epochMonthRM === epochMonth && item.inventoryBomPlan != 0);
  const understockedClasses = categoryLevelData.filter((item) => item.inventoryBomRm < item.inventoryBomPlan && item.epochMonthRM === epochMonth && item.inventoryBomPlan != 0);
  const totalOverstocked = Math.round(overstockedClasses.reduce((acc, item) => acc + (item.inventoryBomRm - item.inventoryBomPlan), 0));
  const totalUnderstocked = Math.round(understockedClasses.reduce((acc, item) => acc + (item.inventoryBomPlan - item.inventoryBomRm), 0));
  const understockTotalSales = understockedClasses.reduce((acc, item) => acc + item.salesRm, 0);
  const understockTotalInventory = understockedClasses.reduce((acc, item) => acc + item.inventoryBomRm, 0);
  const understockTotalInvPlan = understockedClasses.reduce((acc, item) => acc + item.inventoryBomPlan, 0);
  const missedSales = Math.round(PerformanceDashboardService.calculateMissedSales(understockTotalInvPlan, understockTotalInventory, understockTotalSales));


  const compareDateString = "from " + EpochMonthService.epochMonthToMMMYY(compareEpochMonth);

  return (
    <TooltipProvider>
      <div className="flex min-h-screen flex-col bg-gray-100">
        <SelectionHeader />
        <main className="flex-1 container py-6">
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold tracking-tight">Performance Dashboard</h1>
            </div>

            <ExecutiveSummary 
              clientLevelData={clientLevelData}
              categoryLevelData={categoryLevelData}
              epochMonth={epochMonth}
              compareEpochMonth={compareEpochMonth}
              missedSales={missedSales}
              totalOverstocked={totalOverstocked}
            />

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5">
              <QuickComparisonCard
                title="Sales"
                value={thisMonthClientLevelData.salesRm}
                comparisonValue={compareMonthClientLevelData?.salesRm ?? 0}
                description={`Total sales value for the current period. Compares current month sales difference ${compareDateString} sales.`}
                comparisonText={compareDateString}
                icon="DollarSign"
              >
                <>
                  {thisMonthClientLevelData.salesPlan ? 
                    <div className="text-sm text-muted-foreground">
                      <strong>Plan: </strong> {PerformanceDashboardService.moneyFormat(Math.abs(thisMonthClientLevelData.salesPlan), true)}
                    </div>
                  : null}
                  {thisMonthClientLevelData.trend ? 
                    <div className="text-sm text-muted-foreground">
                      <strong>Trend: </strong> {PerformanceDashboardService.moneyFormat(Math.abs(thisMonthClientLevelData.salesRm / thisMonthClientLevelData.trend), true)}
                    </div>
                  : null}
                </>
              </QuickComparisonCard>
              <QuickComparisonCard
                title="Markdowns"
                value={thisMonthClientLevelData.markdownsRm}
                comparisonValue={compareMonthClientLevelData?.markdownsRm ?? 0}
                description={`Total markdowns for the current period. Compares current month markdowns difference ${compareDateString} markdowns.`}
                comparisonText={compareDateString}
                icon="Tag"
              >
                <>
                  {thisMonthClientLevelData.markdownsPlan ? 
                    <div className="text-sm text-muted-foreground">
                      <strong>Plan: </strong> {PerformanceDashboardService.moneyFormat(Math.abs(thisMonthClientLevelData.markdownsPlan), true)}
                    </div>
                  : null}
                  {thisMonthClientLevelData.trend ? 
                    <div className="text-sm text-muted-foreground">
                      <strong>Trend: </strong> {PerformanceDashboardService.moneyFormat(Math.abs(thisMonthClientLevelData.markdownsRm / thisMonthClientLevelData.trend), true)}
                    </div>
                  : null}
                </>
              </QuickComparisonCard>
              <QuickComparisonCard
                title="Inventory"
                value={thisMonthClientLevelData.inventoryBOMRM}
                comparisonValue={compareMonthClientLevelData?.inventoryBOMRM ?? 0}
                description={`Current inventory value. Compares current inventory difference ${compareDateString} inventory.`}
                comparisonText={compareDateString}
                icon="Box"
              >
                <>
                  {thisMonthClientLevelData.inventoryBOMPlan ? <>
                      <div className="text-sm text-muted-foreground">
                        <strong>Plan: </strong> {PerformanceDashboardService.moneyFormat(Math.abs(thisMonthClientLevelData.inventoryBOMPlan), true)}
                      </div>
                      <br/>
                    </>
                  : <><br/><br/></>}
                </>
              </QuickComparisonCard>
              <QuickComparisonCard
                title="Inflow"
                value={thisMonthClientLevelData.inflowRetailRm}
                comparisonValue={compareMonthClientLevelData?.inflowRetailRm ?? 0}
                description={`Total inflow for the current period. Compares current month inflow difference ${compareDateString} inflow.`}
                comparisonText={compareDateString}
                icon="Truck"
              >
                <>
                  {thisMonthClientLevelData.inflowPlanRetailRm ? <>
                      <div className="text-sm text-muted-foreground">
                        <strong>Plan: </strong> {PerformanceDashboardService.moneyFormat(Math.abs(thisMonthClientLevelData.inflowPlanRetailRm), true)}
                      </div>
                      <br/>
                    </>
                  : <><br/><br/></>}
                </>
              </QuickComparisonCard>
              <QuickComparisonCard
                title="Open to Buy (Retail)"
                value={otbRetailEomNext3Months}
                comparisonValue={compareMonthClientLevelData?.otbRetailEom ?? 0}
                description={`Total open to buy value for this month and next two. Compares current month open to buy difference ${compareDateString} open to buy.`}
                comparisonText={compareDateString}
                icon="ShoppingBag"
              >
                <>
                  <span className="text-sm">
                    {`${EpochMonthService.epochMonthToMMMM(epochMonth)}-${EpochMonthService.epochMonthToMMMM(epochMonth + 2)} Total`}
                  </span>
                  <div className="text-sm text-muted-foreground mt-2">
                    <strong>{`${EpochMonthService.epochMonthToMMMM(epochMonth)}`} Projected: </strong> {PerformanceDashboardService.moneyFormat(thisMonthClientLevelData.otbRetailEom, true)}
                  </div>
                </>
              </QuickComparisonCard>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <QuickInfoCard
                title="Total Overstock"
                sidebarColor="red"
                value={totalOverstocked}
                tooltipText="Total overstocked inventory value."
                description="Excess inventory which may require markdowns"
                color="red-500"
                icon="TrendingUpRed"
              />
              <QuickInfoCard
                title="Total Understock"
                sidebarColor="yellow"
                value={totalUnderstocked}
                tooltipText="Total understocked inventory value."
                description="Insufficient inventory that may lead to missed sales"
                color="yellow-500"
                icon="TrendingDownYellow"
              />
              <QuickInfoCard
                title="Total Missed Sales"
                sidebarColor="red"
                value={missedSales}
                tooltipText="Estimated sales lost due to understocking."
                description={
                  <span>
                    <span className="font-bold text-red-500">Urgent:</span> Revenue lost due to stockouts
                  </span>
                }
                color="red-500"
                icon="RedAlert"
              />
            </div>
            <InventoryHealth overstockedClasses={overstockedClasses} understockedClasses={understockedClasses} />
            <ClassPerformance categoryLevelData={categoryLevelData} epochMonth={epochMonth} compareEpochMonth={compareEpochMonth} compareDateString={compareDateString} />
            {locations.length > 1 && locationLevelData && (
              <LocationSalesBreakdown
                locationLevelData={locationLevelData}
                epochMonth={epochMonth}
                compareEpochMonth={compareEpochMonth}
                compareDateString={compareDateString}
                locations={locations}
              />
            )}
          </div>
        </main>
      </div>
    </TooltipProvider>
  )
}